import { Address, LocationSummary, OrderDetails } from "@/types/encomendas";
import { formatLocalityWithState } from "@/utils/formatLocality";

export function onlyDigits(value: string): string {
  return (value || "").replace(/\D/g, "");
}

export function isCep(value: string): boolean {
  const digits = onlyDigits(value);
  return digits.length === 8;
}

export async function fetchViaCepAndFill(
  cepValue: string,
  onFill: (addr: Partial<Address>) => void
) {
  const digits = onlyDigits(cepValue);
  if (digits.length !== 8) return;
  try {
    const resp = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
    if (!resp.ok) return;
    const data = await resp.json();
    if (data.erro) return;
    onFill({
      cep: data.cep || cepValue,
      rua: data.logradouro || "",
      bairro: data.bairro || "",
      cidade: data.localidade || "",
      estado: data.uf || "",
    });
  } catch {
    // ignore
  }
}

function validarCPF(digits: string): boolean {
  if (!digits || digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(digits.charAt(i)) * (10 - i);
  let rev = 11 - (sum % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(digits.charAt(9))) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(digits.charAt(i)) * (11 - i);
  rev = 11 - (sum % 11);
  if (rev === 10 || rev === 11) rev = 0;
  return rev === parseInt(digits.charAt(10));
}

function validarCNPJ(digits: string): boolean {
  if (!digits || digits.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(digits)) return false;
  const calc = (base: number) => {
    let length = base - 7;
    let numbers = digits.substring(0, base);
    let sum = 0;
    let pos = base - 7;
    for (let i = base; i >= 1; i--) {
      sum += parseInt(numbers.charAt(base - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    const result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    return result;
  };
  const d1 = calc(12);
  if (d1 !== parseInt(digits.charAt(12))) return false;
  const d2 = calc(13);
  return d2 === parseInt(digits.charAt(13));
}

export function validarCpfCnpj(valor: string): boolean {
  const digits = onlyDigits(valor);
  if (digits.length === 11) return validarCPF(digits);
  if (digits.length === 14) return validarCNPJ(digits);
  return false;
}

export function maskTelefone(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);
  if (!digits) return "";
  // Don't apply paren formatting until there are at least 3 digits (DDD + first phone digit)
  if (digits.length <= 2) return digits;
  if (digits.length <= 10) {
    // (AA) NNNN-NNNN
    const p1 = digits.slice(0, 2);
    const p2 = digits.slice(2, 6);
    const p3 = digits.slice(6, 10);
    return [p1 && `(${p1}`, p1 && ") ", p2, p3 && "-", p3]
      .filter(Boolean)
      .join("");
  }
  // (AA) NNNNN-NNNN
  const p1 = digits.slice(0, 2);
  const p2 = digits.slice(2, 7);
  const p3 = digits.slice(7, 11);
  return [p1 && `(${p1}`, p1 && ") ", p2, p3 && "-", p3]
    .filter(Boolean)
    .join("");
}
export function maskCpfCnpj(value: string): string {
  const digits = onlyDigits(value).slice(0, 14);
  if (digits.length <= 11) {
    // CPF: 000.000.000-00
    const p1 = digits.slice(0, 3);
    const p2 = digits.slice(3, 6);
    const p3 = digits.slice(6, 9);
    const p4 = digits.slice(9, 11);
    let out = p1;
    if (p2) out += `.${p2}`;
    else return out;
    if (p3) out += `.${p3}`;
    else return out;
    if (p4) out += `-${p4}`;
    return out;
  }
  // CNPJ: 00.000.000/0000-00
  const p1 = digits.slice(0, 2);
  const p2 = digits.slice(2, 5);
  const p3 = digits.slice(5, 8);
  const p4 = digits.slice(8, 12);
  const p5 = digits.slice(12, 14);
  let out = p1;
  if (p2) out += `.${p2}`;
  else return out;
  if (p3) out += `.${p3}`;
  else return out;
  if (p4) out += `/${p4}`;
  else return out;
  if (p5) out += `-${p5}`;
  return out;
}
export function maskCep(value: string): string {
  const digits = onlyDigits(value).slice(0, 8);
  const p1 = digits.slice(0, 5);
  const p2 = digits.slice(5, 8);
  return p2 ? `${p1}-${p2}` : p1;
}
export function validarTelefone(value: string): boolean {
  const digits = onlyDigits(value);
  if (digits.length === 11) {
    // Celular: 11 dígitos e nono dígito = 9
    const [d0, d1, d2] = [digits[0], digits[1], digits[2]];
    if (d0 === "0" || d1 === "0") return false;
    return d2 === "9";
  }
  if (digits.length === 10) {
    // Fixo: 10 dígitos e primeiro dígito do assinante entre 2 e 5
    const [d0, d1, d2] = [digits[0], digits[1], digits[2]];
    if (d0 === "0" || d1 === "0") return false;
    return ["2", "3", "4", "5"].includes(d2);
  }
  return false;
}
export function validarNomeCompleto(value: string): boolean {
  const trimmed = (value || "").trim();
  if (!trimmed) return false;
  const parts = trimmed.split(/\s+/).filter(Boolean);
  return parts.length >= 2;
}

export function formatCurrencyBRL(value: number): string {
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  } catch {
    return `R$ ${value.toFixed(2).replace(".", ",")}`;
  }
}

export function getLocationLabel(loc: LocationSummary): string {
  if ("label" in loc) return loc.label || "";
  const name = (loc as any)?.name ?? "";
  const cityName = (loc as any)?.city_name ?? "";
  return formatLocalityWithState(name, cityName, (loc as any)?.state);
}

// ── Order details (GET /orders/{id}) formatting & status ──────────────────────

export function titleCase(str: string): string {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function getCityAndState(
  point: OrderDetails["origin"] | OrderDetails["destination"]
): string {
  if (!point) return "-";
  return formatLocalityWithState(
    point.name,
    (point as { city_name?: string }).city_name,
    point.state
  );
}

export function formatAddress(
  point: OrderDetails["origin"] | OrderDetails["destination"]
): string {
  if (!point) return "-";
  return point.address;
}

// Takes a value in cents (unlike formatCurrencyBRL which takes reais).
export function formatCentsBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("pt-BR");
}

export function getPaymentMethodLabel(method: string): string {
  const methodMap: Record<string, string> = {
    credit_card: "Cartão de Crédito",
    pix: "Pix",
    apple_pay: "Apple Pay",
    google_pay: "Google Pay",
  };
  return methodMap[method] || method;
}

export const STATUS_STRING_MAP: Record<string, number> = {
  cancelled: 0,
  awaiting_payment: 1,
  paid: 2,
  collected: 3,
  in_transit: 4,
  arrived: 5,
  delivered: 6,
  refunded: 9,
};

export function normalizeStatus(status: number | string): number {
  if (typeof status === "number") return status;
  return STATUS_STRING_MAP[status] ?? -1;
}

export function getStatusConfig(status: number | string): {
  label: string;
  color: string;
  bg: string;
} {
  const map: Record<number, { label: string; color: string; bg: string }> = {
    0: { label: "Cancelado", color: "#E5484D", bg: "#FCDCDD" },
    1: { label: "Aguardando pagamento", color: "#C07B0A", bg: "#FEF3C7" },
    2: { label: "Aguardando envio", color: "#C07B0A", bg: "#FEF3C7" },
    3: { label: "Coletado", color: "#0E7A4D", bg: "#D1FAE5" },
    4: { label: "Em trânsito", color: "#1D60C8", bg: "#DBEAFE" },
    5: { label: "Disponível para retirada", color: "#7C3AED", bg: "#EDE9FE" },
    6: { label: "Entregue", color: "#0E7A4D", bg: "#D1FAE5" },
    9: { label: "Reembolsado", color: "#6C7C93", bg: "#E2E5E9" },
  };
  return (
    map[normalizeStatus(status)] ?? {
      label: "Desconhecido",
      color: "#6C7C93",
      bg: "#E2E5E9",
    }
  );
}

export const TRACKING_STEPS = [
  { label: "Etapa 01", title: "Aguardando pagamento", status: 1 },
  { label: "Etapa 02", title: "Aguardando envio", status: 2 },
  { label: "Etapa 03", title: "Coletado", status: 3 },
  { label: "Etapa 04", title: "Em trânsito", status: 4 },
  { label: "Etapa 05", title: "Disponível para retirada", status: 5 },
  { label: "Etapa 06", title: "Entregue", status: 6 },
];

// ── Order creation ────────────────────────────────────────────────────────────

export const LOADING_MESSAGES = [
  "Estamos processando seu envio...",
  "Só mais um instante...",
  "Quase lá...",
];

// POST /orders with retry/backoff on deadlocks. Throws sentinel messages the
// caller maps to UX: "__auth__" (401) and "__422__:<msg>" (validation).
export async function createOrderWithRetry(
  orderPayload: any,
  token: string,
  maxRetries = 3
): Promise<any> {
  const API = process.env.NEXT_PUBLIC_BASE_URL_API;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch(`${API}/api/v1/embarca-log/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(orderPayload),
      });

      if (!response.ok) {
        let errorData: any = {};
        try {
          errorData = await response.json();
        } catch {}
        const errorCode = errorData.error || "";
        const errorMessage = errorData.message || "";

        if (response.status === 401) {
          throw new Error("__auth__");
        }
        if (response.status === 422) {
          // Prefer the API's human-readable message, fallback to the error code
          throw new Error(`__422__:${errorMessage || errorCode}`);
        }
        if (errorCode.includes("Deadlock") && attempt < maxRetries - 1) {
          await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
          continue;
        }
        throw new Error(
          errorMessage || errorCode || "Falha ao criar o pedido."
        );
      }

      return await response.json();
    } catch (error: any) {
      const msg = error?.message || String(error);
      if (!msg.includes("Deadlock")) throw error;
      if (attempt < maxRetries - 1) {
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
        continue;
      }
      throw error;
    }
  }
  throw new Error("Falha ao criar o pedido após múltiplas tentativas.");
}
